import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { IoEyeOutline, IoFilterOutline, IoCheckmarkCircleOutline, IoCloseCircleOutline } from 'react-icons/io5';
import { useUser } from '../../context/UserContext';
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { formatDate } from '../../utils/format';
import { toast } from 'react-toastify';
import { CgNotes } from "react-icons/cg";


function ManagerPayslipRequests() {
  const navigate = useNavigate();
  const { currentUser, loading } = useUser();
  const [payslipRequests, setPayslipRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [rejectComment, setRejectComment] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [apiLoading, setLoading] = useState(true);
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [isRejectMode, setIsRejectMode] = useState(false);


  // Bulk action states
  const [selectedRequests, setSelectedRequests] = useState([]);
  const [showBulkRejectModal, setShowBulkRejectModal] = useState(false);
  const [bulkRejectComment, setBulkRejectComment] = useState('');
  const [isBulkApproving, setIsBulkApproving] = useState(false);
  const [isBulkRejecting, setIsBulkRejecting] = useState(false);

  const requestsPerPage = 6;
  const baseUrl = process.env.REACT_APP_API_URL;

  // Role-based dynamic configuration
  const isCEO = currentUser?.role === "CEO";
  const roleKey = isCEO ? "ceoId" : "managerId";
  const pageTitle = isCEO ? "Company Payslip Requests" : "Team Payslip Requests";

  const fetchPayslipRequests = async () => {
    if (!currentUser?.id) return;
    setLoading(true);
    try {
      const endpoint = isCEO ? `/payslip/ceo/${currentUser?.id}/requests` : `/payslip/manager/${currentUser?.id}/requests`;
      const response = await api.get(`${endpoint}?page=0&size=50&status=DOWNLOAD_REQUESTED`);
      
      console.log('Payslip requests response:', response.data);
      
      // Handle different response formats
      let requestsData = [];
      if (response.data?.content) {
        requestsData = response.data.content;
      } else if (Array.isArray(response.data)) {
        requestsData = response.data;
      } else {
        console.warn('Unexpected response format:', response.data);
        setPayslipRequests([]);
        return;
      }

      const dataWithIds = requestsData.map(req => ({
        id: req.payid,
        payid: req.payid,
        employeeId: req.employeeId,
        employeeName: req.employeeName,
        salaryPeriod: req.salaryPeriod,
        netPayable: req.netPayable,
        reason: req.downloadreason || 'No reason provided',
        status: 'DOWNLOAD_REQUESTED',
        requestedAt: req.downloadrequestedAt
      }));
      
      console.log('Mapped payslip requests:', dataWithIds);
      setPayslipRequests(dataWithIds);
    } catch (error) {
      console.error('Failed to fetch payslip requests:', error);
      toast.error('Failed to load payslip download requests');
      setPayslipRequests([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser?.id) {
      fetchPayslipRequests();
    }
  }, [currentUser?.id, isCEO]);

  const filteredRequests = payslipRequests.filter(request =>
    statusFilter === 'all' ? true : request.status.toLowerCase() === statusFilter.toLowerCase()
  );

  // Calculate paginated data
  const totalPages = Math.ceil(filteredRequests.length / requestsPerPage);
  const paginatedRequests = filteredRequests.slice(
    (currentPage - 1) * requestsPerPage,
    currentPage * requestsPerPage
  );

  const handleView = (request) => {
    setSelectedRequest(request);
    setRejectComment(""); // reset comment
    setIsRejectMode(false);
    setShowViewModal(true);
  };

  const handleApprove = async (payid) => {
    setIsApproving(true);
    try {
      await api.post(`/payslip/approve-download-request/${payid}?${roleKey}=${currentUser?.id}`);
      toast.success('Payslip download request approved');
      setPayslipRequests(payslipRequests.filter(req => req.payid !== payid));
      if (selectedRequest?.payid === payid) {
        setShowViewModal(false);
      }
      if (filteredRequests.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      }
    } catch (error) {
      console.error('Failed to approve request:', error);
      if (error.response?.status === 403) {
        toast.error('You are not authorized to approve this request');
      } else {
        toast.error('Failed to approve request');
      }
    } finally {
      setIsApproving(false);
    }
  };

  const handleReject = async (payid) => {
    if (!rejectComment.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    setIsRejecting(true);
    try {
      await api.post(`/payslip/reject-download-request/${payid}?${roleKey}=${currentUser?.id}`, {
        reason: rejectComment
      });
      toast.success('Payslip download request rejected');
      setPayslipRequests(payslipRequests.filter(req => req.payid !== payid));
      setRejectComment('');
      setShowViewModal(false);
      if (filteredRequests.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      }
    } catch (error) {
      console.error('Failed to reject request:', error);
      if (error.response?.status === 403) {
        toast.error('You are not authorized to reject this request');
      } else {
        toast.error('Failed to reject request');
      }
    } finally {
      setIsRejecting(false);
    }
  };

  // Bulk action handlers
  const handleSelectRequest = (requestId) => {
    setSelectedRequests(prev =>
      prev.includes(requestId) ? prev.filter(id => id !== requestId) : [...prev, requestId]
    );
  };

  const handleSelectAll = () => {
    if (selectedRequests.length === paginatedRequests.length) {
      setSelectedRequests([]);
    } else {
      setSelectedRequests(paginatedRequests.map(req => req.payid));
    }
  };

const handleBulkApprove = async () => {
  setIsBulkApproving(true);
  try {
    await Promise.all(
      selectedRequests.map(payid =>
        api.post(`/payslip/approve-download-request/${payid}?${roleKey}=${currentUser?.id}`)
      )
    );

    toast.success('All selected requests approved');

    setPayslipRequests(prev =>
      prev.filter(req => !selectedRequests.includes(req.payid))
    );

    setSelectedRequests([]);
  } catch (error) {
    console.error(error);
    toast.error('Failed to approve some requests');
  } finally {
    setIsBulkApproving(false);
  }
};


  const handleBulkReject = async () => {
    if (!bulkRejectComment.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    setIsBulkRejecting(true);
    try {
      for (const payid of selectedRequests) {
        await api.post(`/payslip/reject-download-request/${payid}?${roleKey}=${currentUser?.id}`, {
          reason: bulkRejectComment
        });
      }
      toast.success('All selected requests rejected');
      setPayslipRequests(payslipRequests.filter(req => !selectedRequests.includes(req.payid)));
      setSelectedRequests([]);
      setBulkRejectComment('');
      setShowBulkRejectModal(false);
      if (filteredRequests.length === selectedRequests.length && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      }
    } catch (error) {
      console.error('Failed to bulk reject:', error);
      toast.error('Failed to reject some requests');
    } finally {
      setIsBulkRejecting(false);
    }
  };

  // Add check for currentUser and loading
  useEffect(() => {
    if (!loading && !currentUser) {
      navigate('/login');
    }
  }, [currentUser, loading, navigate]);

  useEffect(() => {
    if (!loading && currentUser) {
      if (!['MANAGER', 'CEO'].includes(currentUser.role)) {
        navigate('/unauthorized');
      }
    }
  }, [currentUser, loading, navigate]);

  // Show loading state
  if (loading || !currentUser) {
    return (
      <div className="sm:p-6 sm:mt-auto mt-8 w-full mx-auto h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
          <p className="text-slate-600">Loading payslip requests...</p>
        </div>
      </div>
    );
  }

  const isActionInProgress = isApproving || isRejecting;
  const isBulkActionInProgress = isBulkApproving || isBulkRejecting;

  return (
    <div className="sm:p-6 sm:mt-auto mt-8 w-full mx-auto">
      <motion.div className="flex justify-between items-center mb-6 bg-slate-200 rounded-2xl p-6" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
        <div className='flex items-center gap-4 text-xl xs:text-2xl font-bold truncate'>
          <div className='p-2 rounded-xl bg-slate-700 text-slate-100'><CgNotes className="text-2xl" /></div>
          <h2 className="text-xl xs:text-2xl font-bold">{pageTitle}</h2>
        </div>
        <div className="flex items-center gap-2 ">
          <IoFilterOutline className="text-slate-600" />
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="bg-white border border-slate-200 text-slate-600 text-sm rounded-full focus:ring-blue-500 focus:border-blue-500 p-2.5"
          >
            <option value="all">All Requests</option>
            <option value="download_requested">Pending</option>
          </select>
        </div>
      </motion.div>

      {/* Bulk Actions */}
      {selectedRequests.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center justify-between"
        >
          <span className="text-blue-900 font-semibold">
            {selectedRequests.length} request(s) selected
          </span>
          <div className="flex gap-3">
            <motion.button
              onClick={handleBulkApprove}
              disabled={isBulkActionInProgress}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-4 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 disabled:bg-slate-400 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <IoCheckmarkCircleOutline size={18} />
              Approve All
            </motion.button>
            <motion.button
              onClick={() => setShowBulkRejectModal(true)}
              disabled={isBulkActionInProgress}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-4 py-2 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 disabled:bg-slate-400 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <IoCloseCircleOutline size={18} />
              Reject All
            </motion.button>
          </div>
        </motion.div>
      )}

      {/* Main Content */}
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gradient-to-r from-slate-100 to-slate-50 border-b border-slate-200">
                {/* <th className="px-4 py-4 text-left">
                  <input
                    type="checkbox"
                    checked={selectedRequests.length === paginatedRequests.length && paginatedRequests.length > 0}
                    onChange={handleSelectAll}
                    className="rounded border-slate-300 cursor-pointer"
                  />
                </th> */}
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-600">Sl. No</th>
                <th className="px-4 py-4 text-left text-sm font-semibold text-slate-700">Employee ID</th>
                <th className="px-4 py-4 text-left text-sm font-semibold text-slate-700">Employee Name</th>
                <th className="px-4 py-4 text-left text-sm font-semibold text-slate-700">Salary Period</th>
                {/* <th className="px-4 py-4 text-left text-sm font-semibold text-slate-700">Net Payable</th> */}
                <th className="px-4 py-4 text-left text-sm font-semibold text-slate-700">Reason</th>
                <th className="px-4 py-4 text-left text-sm font-semibold text-slate-700">Requested At</th>
                <th className="px-4 py-4 text-center text-sm font-semibold text-slate-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedRequests.length > 0 ? (
                paginatedRequests.map((request, index) => (
                  <motion.tr
                    key={request.payid}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="border-b border-slate-200 hover:bg-slate-50 transition-colors"
                  >
                    {/* <td className="px-4 py-4">
                      <input
                        type="checkbox"
                        checked={selectedRequests.includes(request.payid)}
                        onChange={() => handleSelectRequest(request.payid)}
                        className="rounded border-slate-300 cursor-pointer"
                      />
                    </td> */}
                    <td className="px-6 py-4 text-sm text-slate-800 font-medium">{(currentPage - 1) * requestsPerPage + index + 1}</td>
                    <td className="px-4 py-4">
                      <span className="font-medium text-slate-900">{request.employeeId}</span>
                    </td>
                    <td className="px-4 py-4">
                      <span className="font-medium text-slate-900">{request.employeeName}</span>
                    </td>
                    <td className="px-4 py-4 text-slate-600">{request.salaryPeriod}</td>
                    {/* <td className="px-4 py-4">
                      <span className="font-semibold text-green-600">₹{request.netPayable}</span>
                    </td> */}
                    <td className="px-4 py-4 text-sm text-slate-600 max-w-xs truncate" title={request.reason}>
                      {request.reason || 'No reason provided'}
                    </td>
                    <td className="px-4 py-4 text-sm text-slate-600">
                      {request.requestedAt ? formatDate(request.requestedAt) : 'N/A'}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex justify-center gap-2">
                        <motion.button
                          onClick={() => handleView(request)}
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.95 }}
                          className="p-2 hover:bg-blue-100 rounded-lg text-blue-600 transition-colors"
                          title="View Details"
                        >
                          <IoEyeOutline size={18} />
                        </motion.button>
                        <motion.button
                          onClick={() => handleApprove(request.payid)}
                          disabled={isActionInProgress}
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.95 }}
                          className="p-2 hover:bg-green-100 rounded-lg text-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Approve"
                        >
                          <IoCheckmarkCircleOutline size={18} />
                        </motion.button>
                        <motion.button
                          onClick={() => {
                            setSelectedRequest(request);
                            setRejectComment("");
                            setIsRejectMode(true);
                            setShowViewModal(true);
                          }}
                          disabled={isActionInProgress}
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.95 }}
                          className="p-2 hover:bg-red-100 rounded-lg text-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Reject"
                        >
                          <IoCloseCircleOutline size={18} />
                        </motion.button>
                      </div>
                    </td>
                  </motion.tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="px-4 py-12 text-center text-slate-500">
                    No payslip download requests found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-slate-200 flex justify-center">
            <Stack spacing={2}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(e, value) => setCurrentPage(value)}
                color="standard"
              />
            </Stack>
          </div>
        )}
      </div>

      {/* View Modal */}
      {showViewModal && selectedRequest && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setShowViewModal(false)}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6"
          >
            <h3 className="text-2xl font-bold mb-4 text-slate-800">Download Request Details</h3>

            <div className="space-y-4 mb-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-600">Employee</p>
                  <p className="font-semibold text-slate-900">{selectedRequest.employeeName}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Salary Period</p>
                  <p className="font-semibold text-slate-900">{selectedRequest.salaryPeriod}</p>
                </div>
              </div>

              {/* <div>
                <p className="text-sm text-slate-600">Net Payable</p>
                <p className="font-semibold text-green-600 text-lg">₹{selectedRequest.netPayable}</p>
              </div> */}

              <div>
                <p className="text-sm text-slate-600">Reason for Download</p>
                <p className="font-medium text-slate-900 bg-slate-50 p-3 rounded-lg">{selectedRequest.reason}</p>
              </div>

              {selectedRequest.requestedAt && (
                <div>
                  <p className="text-sm text-slate-600">Requested At</p>
                  <p className="font-medium text-slate-900">{formatDate(selectedRequest.requestedAt)}</p>
                </div>
              )}
            </div>
            

            {/* Reject Comment Input */}
            {isRejectMode && (
              <div className="mb-6">
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Rejection Comment (if rejecting)
                </label>
                <textarea
                  value={rejectComment}
                  onChange={(e) => setRejectComment(e.target.value)}
                  placeholder="Enter reason for rejection..."
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
                  rows="3"
                />
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3">
              <motion.button
                onClick={() => setShowViewModal(false)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg font-semibold hover:bg-slate-50 transition-colors"
              >
                Close
              </motion.button>
              <motion.button
                onClick={() => handleReject(selectedRequest.payid)}
                disabled={isActionInProgress || !rejectComment.trim()}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 disabled:bg-slate-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <IoCloseCircleOutline size={18} />
                Reject
              </motion.button>
              <motion.button
                onClick={() => handleApprove(selectedRequest.payid)}
                disabled={isActionInProgress}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 disabled:bg-slate-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <IoCheckmarkCircleOutline size={18} />
                Approve
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Bulk Reject Modal */}
      {showBulkRejectModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setShowBulkRejectModal(false)}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6"
          >
            <h3 className="text-2xl font-bold mb-4 text-slate-800">Reject Selected Requests</h3>
            <p className="text-slate-600 mb-4">
              You are about to reject {selectedRequests.length} request(s). Please provide a reason.
            </p>

            <textarea
              value={bulkRejectComment}
              onChange={(e) => setBulkRejectComment(e.target.value)}
              placeholder="Enter reason for rejection..."
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none mb-6"
              rows="4"
            />

            <div className="flex gap-3">
              <motion.button
                onClick={() => setShowBulkRejectModal(false)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg font-semibold hover:bg-slate-50 transition-colors"
              >
                Cancel
              </motion.button>
              <motion.button
                onClick={handleBulkReject}
                disabled={isBulkRejecting || !bulkRejectComment.trim()}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 disabled:bg-slate-400 disabled:cursor-not-allowed"
              >
                {isBulkRejecting ? 'Rejecting...' : 'Confirm Rejection'}
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
}

export default ManagerPayslipRequests;
